import React from "react";
import { ChevronLeft, ChevronRight, Lock, Unlock, Send, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { colorOf } from "@/lib/timesheetUtils";
import CollaboratorSearch from "@/components/timesheet/CollaboratorSearch";

/**
 * Barre supérieure du timesheet : titre du mois, navigation prev/next, boutons Submit /
 * Approved / Month-settled / Request unlock + dialog d'unlock.
 */
export default function TimesheetHeader({
  monthName,
  onPrev,
  onNext,
  onSubmit,
  showSubmit,
  isApproved,
  monthSettled,
  showSettledBadge,
  settledSummary,
  canRequestUnlock,
  // Iter-124: inline collaborator search (rendered only when `showCollabSearch`).
  showCollabSearch,
  currentUserId,
  // Unlock dialog state (lifted to parent)
  unlockOpen,
  setUnlockOpen,
  unlockMode,
  setUnlockMode,
  unlockMissions,
  setUnlockMissions,
  unlockReason,
  setUnlockReason,
  missions,
  onSendUnlock,
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Timesheet</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">{monthName}</h1>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {showCollabSearch && (
          <CollaboratorSearch currentUserId={currentUserId} />
        )}
        <button
          onClick={onPrev}
          data-testid="prev-month"
          className="w-10 h-10 border border-zinc-200 rounded-md hover:bg-zinc-50 flex items-center justify-center"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          onClick={onNext}
          data-testid="next-month"
          className="w-10 h-10 border border-zinc-200 rounded-md hover:bg-zinc-50 flex items-center justify-center"
        >
          <ChevronRight size={16} />
        </button>

        {showSubmit && (
          <button
            onClick={onSubmit}
            data-testid="submit-cra-btn"
            className="ml-4 inline-flex items-center gap-2 px-5 h-10 bg-zinc-950 text-white rounded-md text-sm hover:bg-zinc-800"
          >
            <Send size={14} /> Submit month
          </button>
        )}

        {isApproved && (
          <div
            className="ml-4 inline-flex items-center gap-2 px-4 h-10 bg-green-50 text-green-700 rounded-md text-sm border border-green-200"
            data-testid="status-approved"
          >
            <Check size={14} /> Approved
          </div>
        )}

        {showSettledBadge && (
          <div
            className="ml-2 inline-flex items-center gap-2 px-4 h-10 bg-emerald-50 text-emerald-800 rounded-md text-sm border border-emerald-200"
            data-testid="month-settled-badge"
            title={`${settledSummary?.paid_invoices || 0}/${settledSummary?.billable_missions || 0} factures clients payées — mois définitivement clos.`}
          >
            <Lock size={14} /> Month closed · paiements reçus
          </div>
        )}

        {canRequestUnlock && (
          <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="unlock-request-btn"
                className="ml-2 inline-flex items-center gap-2 px-5 h-10 bg-white border border-zinc-950 text-zinc-950 rounded-md text-sm hover:bg-zinc-100"
              >
                <Unlock size={14} /> Request unlock
              </button>
            </DialogTrigger>
            <DialogContent data-testid="unlock-dialog">
              <DialogHeader>
                <DialogTitle>Request to unlock {monthName}</DialogTitle>
                <DialogDescription>Choose who should be notified.</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <RadioGroup value={unlockMode} onValueChange={setUnlockMode}>
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="GLOBAL" id="rb-global" data-testid="rb-global" />
                    <Label htmlFor="rb-global" className="flex-1 cursor-pointer">
                      <div className="font-medium">Global</div>
                      <div className="text-xs text-zinc-500 mt-1">Notify every manager whose mission appears in this month.</div>
                    </Label>
                  </div>
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="SELECTIVE" id="rb-selective" data-testid="rb-selective" />
                    <Label htmlFor="rb-selective" className="flex-1 cursor-pointer">
                      <div className="font-medium">Selective</div>
                      <div className="text-xs text-zinc-500 mt-1">Pick specific missions to notify only those managers.</div>
                    </Label>
                  </div>
                </RadioGroup>

                {unlockMode === "SELECTIVE" && (
                  <div className="space-y-2 pl-6 border-l border-zinc-200">
                    {missions.map((m) => (
                      <label
                        key={m.mission_id}
                        className="flex items-center gap-3 text-sm cursor-pointer"
                        data-testid={`unlock-pick-${m.mission_id}`}
                      >
                        <Checkbox
                          checked={unlockMissions.includes(m.mission_id)}
                          onCheckedChange={(v) => {
                            setUnlockMissions((prev) =>
                              v ? [...prev, m.mission_id] : prev.filter((x) => x !== m.mission_id)
                            );
                          }}
                        />
                        <span className="w-2 h-2 rounded-full" style={{ background: colorOf(m) }} />
                        <span>{m.title}</span>
                      </label>
                    ))}
                  </div>
                )}

                <div>
                  <Label htmlFor="reason">Reason (optional)</Label>
                  <Textarea
                    id="reason"
                    data-testid="unlock-reason"
                    value={unlockReason}
                    onChange={(e) => setUnlockReason(e.target.value)}
                    placeholder="Why do you need to edit this month?"
                  />
                </div>
              </div>
              <DialogFooter>
                <button
                  onClick={() => setUnlockOpen(false)}
                  className="px-4 h-10 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  data-testid="unlock-send-btn"
                  onClick={onSendUnlock}
                  className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
                >
                  Send request
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
